import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrustBadge } from "@/components/TrustBadge";
import { Building2, Loader2, ShieldAlert, ShieldCheck, ShieldQuestion } from "lucide-react";

export const VerificationCard = ({ model, user, verifying, selfieRef, onUploadSelfie }) => (
  <Card data-testid="model-form-verification-card" className="mt-6 rounded-2xl">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center justify-between gap-2 text-base">
        <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Identity verification</span>
        <TrustBadge trust={model.trust} />
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      {model.owner_role === "agency" || user?.role === "agency" ? (
        <div className="flex items-start gap-3 rounded-xl border border-[hsl(28,55%,65%)] bg-[hsl(28,55%,95%)] p-4 text-sm">
          <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(28,55%,30%)]" />
          <div>
            <p className="font-semibold text-[hsl(28,55%,22%)]">Agency-represented — automatically trusted</p>
            <p className="text-[hsl(28,45%,30%)]">
              Models on an agency roster are pre-vetted by their agency, so they carry the
              Agency-represented badge in the marketplace and no selfie verification is needed.
            </p>
          </div>
        </div>
      ) : (
        <>
          {model.verification?.status === "verified" ? (
            <div data-testid="verification-result-verified" className="flex items-start gap-3 rounded-xl border-2 border-[hsl(174,38%,70%)] bg-[hsl(174,38%,96%)] p-4 text-sm">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(174,38%,34%)]" />
              <div>
                <p className="font-semibold text-[hsl(174,45%,18%)]">Identity verified</p>
                <p className="text-muted-foreground">
                  Your likeness was matched against your portfolio
                  {model.verification?.confidence ? ` with ${Math.round(model.verification.confidence * 100)}% confidence` : ""}.
                  Your profile carries the Verified badge, and clients can filter for verified looks.
                </p>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Verify you are the person in your portfolio by uploading a clear selfie of your face. Our AI
                matches it against your photos — verified profiles get the{" "}
                <span className="font-semibold text-foreground">Verified</span> badge and rank higher in client
                trust filters. Your selfie is stored privately and never shown publicly.
              </p>
              {model.verification?.status === "rejected" && (
                <div data-testid="verification-result-rejected" className="flex items-start gap-3 rounded-xl border border-[hsl(6,62%,72%)] bg-[hsl(6,62%,95%)] p-4 text-sm">
                  <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                  <div>
                    <p className="font-semibold text-[hsl(6,55%,25%)]">Verification did not pass</p>
                    <p className="text-[hsl(6,40%,30%)]">{model.verification?.reasoning}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Try a well-lit, front-facing selfie without sunglasses or heavy filters.
                    </p>
                  </div>
                </div>
              )}
              <Button
                type="button"
                data-testid="verification-upload-button"
                disabled={verifying || (model.images || []).length === 0}
                onClick={() => selfieRef.current?.click()}
                className="rounded-xl"
              >
                {verifying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> AI is comparing your selfie… (15–45s)
                  </>
                ) : (
                  <>
                    <ShieldQuestion className="mr-2 h-4 w-4" />
                    {model.verification?.status === "rejected" ? "Retry with a new selfie" : "Upload verification selfie"}
                  </>
                )}
              </Button>
              {(model.images || []).length === 0 && (
                <p className="text-[11px] text-muted-foreground">Add at least one portfolio photo first.</p>
              )}
              <input ref={selfieRef} data-testid="verification-file-input" type="file" accept="image/jpeg,image/png,image/webp" hidden
                onChange={(e) => onUploadSelfie(e.target.files)} />
            </>
          )}
        </>
      )}
    </CardContent>
  </Card>
);
